import React, { useEffect, useState, useRef } from "react";
import { 
  Box, 
  Button, 
  CircularProgress, 
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  useGetPrivacyPolicyQuery,
  useUpdatePrivacyPolicyMutation,
} from "../../../Services/policy/Policy";

const PolicyContentEditor = () => {
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const quillRef = useRef<any>(null);

  // Fetch privacy policy from RTK Query
  const {
    data: privacyPolicy,
    isLoading,
    isFetching,
    refetch,
  } = useGetPrivacyPolicyQuery({});

  // Update mutation
  const [updatePolicy] = useUpdatePrivacyPolicyMutation();

  // Populate editor when data changes
  useEffect(() => {
    if (privacyPolicy?.data?.privacy_policy) {
      setContent(privacyPolicy.data?.privacy_policy || "");
    }
  }, [privacyPolicy]);

  // Force karbon-medium inside editor content
  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const editorEl = editor.root;
      if (editorEl) {
        editorEl.style.fontFamily = "karbon-medium";
        editorEl.style.fontSize = "13px";
        editorEl.style.color = "#333";
      }
    }
  }, [quillRef, isLoading, isFetching]);

  const handleOpenModal = () => {
    const plainText = content.replace(/<[^>]+>/g, "").trim();
    if (!plainText) {
      toast.warning("Privacy policy content cannot be empty!");
      return;
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleConfirmSave = async () => {
    setOpenModal(false);
    setSaving(true);
    try {
      const payload = { privacy_policy: content };
      await updatePolicy({ body: payload }).unwrap();
      toast.success("Privacy Policy updated successfully!");
      await refetch();
    } catch (error) {
      let message = "Failed to save Privacy Policy";
      if (error?.data?.error) message = error.data.error;
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress style={{ color: "#FF6B35" }} />
      </Box>
    );
  }

  // ReactQuill formats
  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "color",
    "background",
    "align",
    "script",
    "code-block",
  ];

  // ReactQuill toolbar config
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }, { font: [] }, { size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "code-block"],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["clean"],
    ],
  };

  return (
    <Box>
      <Typography
        variant="h6"
        fontWeight={200}
        mb={2}
        style={{
          fontFamily: "karbon-medium",
          color: "#666666",
          fontSize: "16px",
        }}
      >
        Privacy Policy
      </Typography>

      <div
        style={{
          backgroundColor: "#fff",
          padding: 2,
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ReactQuill
          ref={quillRef}
          value={content}
          onChange={setContent}
          theme="snow"
          modules={modules}
          formats={formats}
          style={{
            height: "200px",
            fontFamily: "karbon-medium",
            color: "#333",
          }}
        />
      </div>

      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleOpenModal}
          disabled={saving}
          style={{
            fontFamily: "karbon-medium",
            padding: "8px 24px",
            fontWeight: 200,
            borderRadius: 8,
            textTransform: "none",
            fontSize: "13px",
            backgroundColor: "#FF6B35",
            color: "#FFFFFF",
          }}
          sx={{
            '&:hover': {
              backgroundColor: "#E55A2B",
            },
            '&:disabled': {
              backgroundColor: "#FFB399",
              color: "#FFFFFF",
            }
          }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      {/* Confirmation Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 12,
            padding: "4px",
            backgroundColor: "#FFF9F7",
          }
        }}
      >
        <DialogTitle 
          style={{ 
            fontFamily: "karbon-medium",
            color: "#333333",
            fontWeight: 600,
            fontSize: "18px",
            padding: "20px 24px 16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Confirm Save Changes
          <IconButton
            onClick={handleCloseModal}
            size="small"
            style={{ color: "#999999" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent style={{ padding: "0 24px 24px 24px" }}>
          <DialogContentText 
            style={{ 
              fontFamily: "karbon-medium",
              color: "#666666",
              fontSize: "14px",
              lineHeight: "1.6",
              marginBottom: "24px",
            }}
          >
            Are you sure you want to save these changes to Privacy Policy? 
            This will update the content for all users and cannot be undone immediately.
          </DialogContentText>
        </DialogContent>

        <DialogActions 
          style={{ 
            padding: "0 24px 20px 24px",
            gap: "12px",
          }}
        >
          <Button 
            onClick={handleCloseModal}
            variant="outlined"
            fullWidth
            style={{
              fontFamily: "karbon-medium",
              textTransform: "none",
              borderColor: "#DDDDDD",
              color: "#666666",
              borderRadius: 8,
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: 500,
            }}
            sx={{
              '&:hover': {
                borderColor: "#CCCCCC",
                backgroundColor: "#F5F5F5",
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSave}
            variant="contained"
            fullWidth
            autoFocus
            style={{
              fontFamily: "karbon-medium",
              textTransform: "none",
              backgroundColor: "#FF6B35",
              color: "#FFFFFF",
              borderRadius: 8,
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: 500,
            }}
            sx={{
              '&:hover': {
                backgroundColor: "#E55A2B",
              }
            }}
          >
            Update Privacy Policy
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PolicyContentEditor;